import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, xcss, Box } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { SpotlightTarget } from '@atlaskit/onboarding';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import {
	GeneralAccessLevelSelector,
	type GeneralAccessLevelSelectorProps,
} from './GeneralAccessLevelSelector.tsx';
import {
	GeneralAccessPermissionDropdown,
	type GeneralAccessPermissionDropdownProps,
} from './GeneralAccessPermissionDropdown.tsx';

const i18n = defineMessages({
	generalAccessTitle: {
		id: 'share-and-restrict-dialog.general-access.title',
		defaultMessage: 'General access',
		description:
			'Section heading for controls that determine whether non-specific users are able to access this content',
	},
	generalAccessInfoIconLabelText: {
		id: 'share-and-restrict-dialog.general-access.info-icon.label-text',
		defaultMessage: 'Additional information',
		description: 'Label text the Info icon next to General Access heading',
	},
	generalAccessInfoIconMessage: {
		id: 'share-and-restrict-dialog.general-access.info-icon.message',
		defaultMessage: "You don't have permission to change access to this content.",
		description: 'Tooltip message for the Info icon next to General Access heading',
	},
});

export type GeneralAccessProps = {
	generalAccessLevelSelectorProps: GeneralAccessLevelSelectorProps;
	generalAccessPermissionDropdownProps: GeneralAccessPermissionDropdownProps | undefined;
	shouldShowUneditableRestrictionsInfoIcon: boolean;
};

const generalAccessSectionContainer = xcss({
	paddingTop: 'space.100',
});

const bottomPadding = xcss({
	paddingBlockEnd: 'space.100',
});

const levelSelectorSpotLightContainer = xcss({
	padding: 'space.075',
	marginInlineStart: 'space.negative.075',
});

const dropdownSpotLightContainer = xcss({
	padding: 'space.075',
	marginInlineEnd: 'space.negative.075',
});

const headingStyles = xcss({
	maxHeight: '16px',
});

export const GeneralAccess = ({
	generalAccessLevelSelectorProps,
	generalAccessPermissionDropdownProps,
	shouldShowUneditableRestrictionsInfoIcon,
	showBottomPadding,
}: GeneralAccessProps & { showBottomPadding: boolean }) => {
	const { formatMessage } = useIntl();

	return (
		<Stack grow="fill">
			<Heading size="xxsmall" as="h2">
				<Inline space="space.050" grow="hug" xcss={headingStyles}>
					{formatMessage(i18n.generalAccessTitle)}
					{shouldShowUneditableRestrictionsInfoIcon && (
						<Tooltip content={formatMessage(i18n.generalAccessInfoIconMessage)} position="right">
							<InformationCircleIcon
								label={formatMessage(i18n.generalAccessInfoIconLabelText)}
								size="small"
							/>
						</Tooltip>
					)}
				</Inline>
			</Heading>
			<Inline
				grow="fill"
				spread="space-between"
				xcss={[generalAccessSectionContainer, showBottomPadding && bottomPadding]}
			>
				<SpotlightTarget name="generalAccess">
					<Box xcss={levelSelectorSpotLightContainer}>
						<GeneralAccessLevelSelector {...generalAccessLevelSelectorProps} />
					</Box>
				</SpotlightTarget>
				{generalAccessPermissionDropdownProps && (
					<SpotlightTarget name="permissionDropdown">
						<Box xcss={dropdownSpotLightContainer}>
							<GeneralAccessPermissionDropdown {...generalAccessPermissionDropdownProps} />
						</Box>
					</SpotlightTarget>
				)}
			</Inline>
		</Stack>
	);
};
